import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Modal from './ui/Modal'
import Button from './ui/Button'
import AscapMark from './AscapMark'

const LOGO_BASE = '/logos/data-upload'

type PartnerId =
  | 'uta'
  | 'caa'
  | 'wme'
  | 'the-team'
  | 'ticketmaster'
  | 'axs'
  | 'eventbrite'
  | 'ascap'
  | 'bmi'
  | 'sesac'
  | 'socan'

const PARTNER_LABEL: Record<PartnerId, string> = {
  uta: 'UTA',
  caa: 'CAA',
  wme: 'WME',
  'the-team': 'The Team',
  ticketmaster: 'Ticketmaster',
  axs: 'AXS',
  eventbrite: 'Eventbrite',
  ascap: 'ASCAP',
  bmi: 'BMI',
  sesac: 'SESAC',
  socan: 'SOCAN',
}

const PARTNER_FILE: Record<PartnerId, string> = {
  uta: 'uta.svg',
  caa: 'caa.svg',
  wme: 'wme.svg',
  'the-team': 'the-team.svg',
  ticketmaster: 'ticketmaster.svg',
  axs: 'axs.svg',
  eventbrite: 'eventbrite.svg',
  ascap: 'ascap.svg',
  bmi: 'bmi.svg',
  sesac: 'sesac.svg',
  socan: 'socan.svg',
}

/** 30% smaller than default slot (0.7 × max-h-8 / max-w) */
const COMPACT_LOGO_IDS = new Set<PartnerId>(['caa', 'axs'])

/** Slightly larger slot (wide viewBox wordmark reads small at default) */
const EXPANDED_LOGO_IDS = new Set<PartnerId>(['wme'])

/** Dark-mode: raster/SVG-as-img → white via invert (SOCAN / ASCAP use inline marks for brand colors). */
const DARK_WHITE_FILTER_IDS = new Set<PartnerId>(['ticketmaster', 'wme'])

/** SOCAN: black “S”/“AN” → white in dark; middle path stays brand red #D02139. */
function SocanMark({ className = '' }: { className?: string }) {
  return (
    <span
      className={`inline-flex h-8 w-[4.5rem] shrink-0 items-center justify-center overflow-visible dark:drop-shadow-[0_0.5px_0_rgba(0,0,0,0.5)] ${className}`}
    >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 116 25"
      fill="none"
      fillRule="evenodd"
      className="block h-full w-full max-h-8 max-w-full"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
      focusable="false"
    >
      <path
        className="fill-gray-900 dark:fill-white"
        d="M2.68 19.238c1.153 1.73 3.155 2.53 5.088 2.53 2.137 0 4.782-1.332 4.782-3.928C12.55 12.415.713 15.776.713 6.59.713 3.828 3.087 0 8.853 0c2.612 0 5.122.533 6.92 2.697L13.127 5.06c-.815-1.232-2.41-2.065-4.275-2.065-3.595 0-4.68 2.13-4.68 3.595 0 5.958 11.838 2.397 11.838 10.883 0 4.56-3.732 7.289-8.344 7.289C4.58 24.762 1.629 23.73 0 21.5l2.68-2.262M39.701 12.381c0-5.158-3.505-9.386-8.91-9.386-5.409 0-8.913 4.228-8.913 9.386 0 5.159 3.504 9.386 8.912 9.386 5.406 0 8.911-4.227 8.911-9.386Zm-21.227 0C18.474 5.392 23.58 0 30.79 0 38 0 43.105 5.392 43.105 12.381c0 6.99-5.106 12.38-12.315 12.38-7.21 0-12.316-5.39-12.316-12.38Z"
      />
      <path fill="#D02139" d="M63.358 5.691c-1.325-1.73-3.512-2.696-5.665-2.696-5.268 0-8.745 4.228-8.745 9.586 0 4.959 3.278 9.186 8.745 9.186 2.684 0 4.737-1.198 6.129-3.162l2.683 2.03c-.696.965-3.512 4.127-8.878 4.127-7.454 0-12.059-6.257-12.059-12.18C45.568 5.258 50.571 0 57.792 0c3.081 0 6.327 1.164 8.216 3.661l-2.65 2.03" />
      <path
        className="fill-gray-900 dark:fill-white"
        d="m83.87 16.157-4.4-10.799h-.066l-4.467 10.799h8.934ZM78.137 1.238h2.9l10.1 23.524h-3.732l-2.367-5.815H73.803l-2.333 5.815h-3.733l10.4-23.524ZM94.832 1.238h4.296l13.299 19.337h.066V1.238h3.275v23.524h-4.16L98.173 5.424h-.068v19.338h-3.273V1.238"
      />
    </svg>
    </span>
  )
}

/** Modest bump over default 18px; compact cell. */
function TheTeamWordmark({ className = '' }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 200 40"
      fill="none"
      preserveAspectRatio="xMidYMid meet"
      className={`block h-[1.875rem] w-auto max-w-none shrink-0 text-gray-900 dark:text-white ${className}`}
      aria-hidden
    >
      <text
        x="100"
        y="26"
        textAnchor="middle"
        fill="currentColor"
        style={{ fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif' }}
        fontSize="18"
        fontWeight="700"
        letterSpacing="0.04em"
      >
        THE TEAM
      </text>
    </svg>
  )
}

function PartnerList({ ids }: { ids: PartnerId[] }) {
  return (
    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
      {ids.map((id) => PARTNER_LABEL[id]).join(', ')}
    </span>
  )
}

/**
 * Uniform logo slots: fixed outer box so wide/tall marks (CAA, AXS, BMI) don’t dominate.
 * Light mode: Tailwind gray-200 (reads as a soft gray on white cards).
 * Dark mode: mid charcoal aligned with app tokens — Tailwind gray-200 is ~#e5e7eb and reads “white” on
 * #13120A; use a raised surface tint so boxes look gray, not a light panel.
 */
function PartnerLogoRow({ ids, className = '' }: { ids: PartnerId[]; className?: string }) {
  return (
    <div className={`flex min-w-0 w-full max-w-full flex-wrap items-center justify-start gap-2 ${className}`} aria-hidden>
      {ids.map((id) => (
        <div
          key={id}
          className={`flex flex-shrink-0 items-center justify-center rounded-md border border-gray-300/90 bg-gray-200 px-2 py-1.5 shadow-sm dark:border-[rgb(var(--border-rgb))] dark:bg-[rgb(52,51,42)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] ${
            id === 'the-team'
              ? 'h-11 w-[6.25rem]'
              : EXPANDED_LOGO_IDS.has(id)
                ? 'h-12 w-[7.25rem]'
                : 'h-11 w-[5.75rem]'
          }`}
          title={PARTNER_LABEL[id]}
        >
          {id === 'the-team' ? (
            <TheTeamWordmark className="dark:drop-shadow-[0_0.5px_0_rgba(0,0,0,0.5)]" />
          ) : id === 'socan' ? (
            <SocanMark />
          ) : id === 'ascap' ? (
            <AscapMark />
          ) : (
            <img
              src={`${LOGO_BASE}/${PARTNER_FILE[id]}`}
              alt=""
              className={`w-auto object-contain object-center dark:drop-shadow-[0_0.5px_0_rgba(0,0,0,0.5)] ${
                COMPACT_LOGO_IDS.has(id)
                  ? 'max-h-[1.55rem] max-w-[3.5rem] min-h-[1.25rem] min-w-[2.75rem]'
                  : EXPANDED_LOGO_IDS.has(id)
                    ? 'max-h-11 max-w-[6.5rem]'
                    : 'max-h-8 max-w-[4.5rem]'
              } ${DARK_WHITE_FILTER_IDS.has(id) ? 'dark:brightness-0 dark:invert' : ''}`}
            />
          )}
        </div>
      ))}
    </div>
  )
}

function SourceChip({
  id,
  active,
  onClick,
}: {
  id: PartnerId
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
        active
          ? 'border-gray-900 bg-gray-900 text-white dark:border-white dark:bg-white dark:text-gray-900'
          : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-gray-500'
      }`}
    >
      {PARTNER_LABEL[id]}
    </button>
  )
}

type Flow = 'root' | 'live-shows' | 'ticket-counts' | 'pro'

type Props = {
  isOpen: boolean
  onClose: () => void
}

export default function UploadDataModal({ isOpen, onClose }: Props) {
  const navigate = useNavigate()
  const [flow, setFlow] = useState<Flow>('root')
  const [liveSource, setLiveSource] = useState<PartnerId | null>(null)
  const [ticketSource, setTicketSource] = useState<PartnerId | null>(null)
  const [proSource, setProSource] = useState<PartnerId | null>(null)
  const [proFileName, setProFileName] = useState<string | null>(null)
  const proInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isOpen) return
    setFlow('root')
    setLiveSource(null)
    setTicketSource(null)
    setProSource(null)
    setProFileName(null)
  }, [isOpen])

  const resetAndClose = () => {
    setFlow('root')
    setLiveSource(null)
    setTicketSource(null)
    setProSource(null)
    setProFileName(null)
    onClose()
  }

  const goShows = (payload: {
    addView: 'csv' | 'ticket-counter'
    ticketImportMode?: boolean
    documentSource: string
    uploadKind: 'live-shows' | 'ticket-counts'
  }) => {
    try {
      sessionStorage.setItem('dizeeUploadDocumentSource', payload.documentSource)
      sessionStorage.setItem('dizeeUploadKind', payload.uploadKind)
    } catch {
      /* ignore */
    }
    navigate('/shows', {
      state: {
        openShowUpload: true,
        addView: payload.addView,
        ticketImportMode: payload.ticketImportMode ?? false,
        documentSource: payload.documentSource,
        uploadKind: payload.uploadKind,
      },
    })
    resetAndClose()
  }

  const liveAgencies: PartnerId[] = ['uta', 'caa', 'wme', 'the-team']
  const ticketDocTypes: PartnerId[] = ['uta', 'caa', 'wme', 'the-team', 'ticketmaster', 'eventbrite']
  const proTypes: PartnerId[] = ['ascap', 'bmi', 'sesac', 'socan']

  const title =
    flow === 'root'
      ? 'Upload Data'
      : flow === 'live-shows'
        ? 'Live Shows Upload'
        : flow === 'ticket-counts'
          ? 'Ticket Counts Upload'
          : 'PRO Statement Upload'

  const description =
    flow === 'root'
      ? 'Choose the type of data you are uploading.'
      : flow === 'live-shows'
        ? 'Pick your booking agency document type, then upload your file.'
        : flow === 'ticket-counts'
          ? 'Pick where this report came from, then continue with ticket import.'
          : 'Pick your PRO, then upload your statement.'

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title={title}
      description={description}
      maxWidthClassName="max-w-2xl"
      mobileCentered
    >
      {flow === 'root' ? (
        <div className="space-y-6">
          {/* ── Shows ── */}
          <div>
            <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
              Shows
            </h3>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setFlow('live-shows')}
                className="w-full text-left rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Live Shows (Upcoming &amp; Past)
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Upload live show data from your booking agency — dates, venues, guarantees, bonuses, set times, etc.
                </p>
                <div className="flex flex-wrap items-baseline gap-x-1.5 mt-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Optimized for</span>
                  <PartnerList ids={liveAgencies} />
                </div>
                <PartnerLogoRow ids={liveAgencies} className="mt-3 pointer-events-none" />
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
                  Recommended for artists, managers, agents &amp; touring teams
                </p>
              </button>

              <button
                type="button"
                onClick={() => setFlow('ticket-counts')}
                className="w-full text-left rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Ticket Counts (Upcoming &amp; Past)
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Upload daily ticket sales reports from booking agencies and ticketing platforms.
                </p>
                <div className="flex flex-wrap items-baseline gap-x-1.5 mt-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Optimized for</span>
                  <PartnerList ids={['uta', 'caa', 'wme', 'the-team', 'ticketmaster', 'axs', 'eventbrite']} />
                </div>
                <PartnerLogoRow
                  ids={['uta', 'caa', 'wme', 'the-team', 'ticketmaster', 'axs', 'eventbrite']}
                  className="mt-3 pointer-events-none"
                />
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
                  Recommended for artists, managers, agents, promoters &amp; touring teams
                </p>
              </button>
            </div>
          </div>

          {/* ── Music ── */}
          <div>
            <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
              Music
            </h3>
            <button
              type="button"
              onClick={() => setFlow('pro')}
              className="w-full text-left rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors"
            >
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Performance Rights Data (PROs)
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Upload PRO statements to fill in writer credits, song splits, publishing info &amp; other metadata.
              </p>
              <div className="flex flex-wrap items-baseline gap-x-1.5 mt-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">Supported</span>
                <PartnerList ids={proTypes} />
              </div>
              <PartnerLogoRow ids={proTypes} className="mt-3 pointer-events-none" />
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
                Recommended for artists, managers, labels &amp; publishers
              </p>
            </button>
          </div>
        </div>
      ) : null}

      {flow === 'live-shows' ? (
        <div className="space-y-5">
          <button
            type="button"
            onClick={() => { setFlow('root'); setLiveSource(null) }}
            className="text-xs text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
          >
            ← Back
          </button>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Choose which document type you are uploading.
          </p>
          <div className="flex min-w-0 w-full flex-wrap gap-2">
            {liveAgencies.map((id) => (
              <SourceChip key={id} id={id} active={liveSource === id} onClick={() => setLiveSource(id)} />
            ))}
          </div>
          <PartnerLogoRow ids={liveAgencies} className="pt-1 pointer-events-none" />
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              disabled={!liveSource}
              onClick={() => {
                if (!liveSource) return
                goShows({ addView: 'csv', documentSource: liveSource, uploadKind: 'live-shows' })
              }}
            >
              Upload Live Shows
            </Button>
          </div>
        </div>
      ) : null}

      {flow === 'ticket-counts' ? (
        <div className="space-y-5">
          <button
            type="button"
            onClick={() => { setFlow('root'); setTicketSource(null) }}
            className="text-xs text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
          >
            ← Back
          </button>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Choose which document type you are uploading.
          </p>
          <div className="flex min-w-0 w-full flex-wrap gap-2">
            {ticketDocTypes.map((id) => (
              <SourceChip
                key={id}
                id={id}
                active={ticketSource === id}
                onClick={() => {
                  setTicketSource(id)
                  goShows({ addView: 'ticket-counter', ticketImportMode: true, documentSource: id, uploadKind: 'ticket-counts' })
                }}
              />
            ))}
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500">Select a source to continue to ticket upload.</p>
        </div>
      ) : null}

      {flow === 'pro' ? (
        <div className="space-y-5">
          <button
            type="button"
            onClick={() => { setFlow('root'); setProSource(null); setProFileName(null) }}
            className="text-xs text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
          >
            ← Back
          </button>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Choose which document type you are uploading.
          </p>
          <div className="flex min-w-0 w-full flex-wrap gap-2">
            {proTypes.map((id) => (
              <SourceChip key={id} id={id} active={proSource === id} onClick={() => setProSource(id)} />
            ))}
          </div>
          <PartnerLogoRow ids={proTypes} className="pt-1 pointer-events-none" />
          <input
            ref={proInputRef}
            type="file"
            accept=".pdf,.csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              setProFileName(f?.name ?? null)
              if (proSource && f) {
                try {
                  sessionStorage.setItem('dizeeUploadDocumentSource', proSource)
                  sessionStorage.setItem('dizeeUploadKind', 'pro-statement')
                } catch {
                  /* ignore */
                }
                navigate('/money', { state: { openMoneyAdd: true } })
                resetAndClose()
              }
              e.target.value = ''
            }}
          />
          <div className="pt-2 space-y-2">
            <Button
              type="button"
              disabled={!proSource}
              onClick={() => proInputRef.current?.click()}
            >
              Upload PRO Statement
            </Button>
            {proFileName ? (
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Selected: <span className="font-medium text-gray-900 dark:text-gray-200">{proFileName}</span>
              </p>
            ) : (
              <p className="text-xs text-gray-400 dark:text-gray-500">PDF, CSV, or Excel</p>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  )
}
